import { Modal } from '@components/ui';
import { CheckCircleIcon, ClockIcon, EyeIcon } from '@heroicons/react/24/outline';
import { CTA, GHOST } from '@components/ui/designTokens';
import { cn } from '@lib/utils/cn';
import { useNavigate } from 'react-router-dom';

interface SuccessModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  message: string;
  status: 'success' | 'pending';
  responseCode?: string;
  additionalInfo?: string;
  electronicId?: string;
}

export function SuccessModal({
  isOpen,
  onClose,
  title,
  message,
  status,
  responseCode,
  additionalInfo,
  electronicId
}: SuccessModalProps) {
  const isSuccess = status === 'success';
  const navigate = useNavigate();

  const handleViewInvoice = () => {
    if (electronicId) {
      navigate(`/pages/invoice/${electronicId}`);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      kicker={isSuccess ? 'Factura · Emitida' : 'Factura · En proceso'}
      title={title}
      icon={isSuccess ? <CheckCircleIcon /> : <ClockIcon />}
      size="lg"
      data-testid="invoice-wizard-success-modal"
      footer={
        <div className="ml-auto flex items-center gap-2.5">
          {electronicId && (
            <button
              type="button"
              onClick={handleViewInvoice}
              data-testid="invoice-wizard-success-modal-view-invoice-button"
              className={GHOST}
            >
              <EyeIcon className="h-4 w-4" />
              Ver Factura
            </button>
          )}
          <button
            type="button"
            onClick={onClose}
            data-testid="invoice-wizard-success-modal-close-button"
            className={cn(CTA)}
          >
            Aceptar
          </button>
        </div>
      }
    >
      <div className="space-y-4">
        {/* Success/Pending Icon and Main Message */}
        <div className="flex items-start space-x-3">
          <div className="flex-shrink-0">
            {isSuccess ? (
              <CheckCircleIcon className="h-8 w-8 text-success-500" />
            ) : (
              <ClockIcon className="h-8 w-8 text-warning-500" />
            )}
          </div>
          <div className="flex-1">
            <p className="text-sm text-secondary-700 dark:text-secondary-300">
              {message}
            </p>
          </div>
        </div>

        {/* Response Code */}
        {responseCode && (
          <div className={`mt-4 rounded-lg p-3 ${
            isSuccess
              ? 'bg-success-50 dark:bg-success-900/20 border border-success-200 dark:border-success-800'
              : 'bg-warning-50 dark:bg-warning-900/20 border border-warning-200 dark:border-warning-800'
          }`}>
            <div className="flex items-center space-x-2">
              <span className={`text-xs font-medium ${
                isSuccess
                  ? 'text-success-800 dark:text-success-200'
                  : 'text-warning-800 dark:text-warning-200'
              }`}>
                Código de respuesta:
              </span>
              <span className={`text-sm font-semibold ${
                isSuccess
                  ? 'text-success-900 dark:text-success-100'
                  : 'text-warning-900 dark:text-warning-100'
              }`}>
                {responseCode}
              </span>
            </div>
          </div>
        )}

        {/* Additional Information */}
        {additionalInfo && (
          <div className="mt-3">
            <p className="text-xs text-secondary-600 dark:text-secondary-400">
              {additionalInfo}
            </p>
          </div>
        )}

      </div>
    </Modal>
  );
}
